import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import { useDeals } from "@/pages/deals/hooks";
import {
  useProposals,
  useProposalVersions,
  useStoredProposalFile
} from "../hooks";
import { withRecipientStates } from "../tracking";
import { earlierSentVersions, versionAsProposal } from "../versionAsProposal";
import { Proposal, ProposalHistoryVersion, ProposalStatus } from "../types";
import { RecipientStatusList } from "../recipients/RecipientStatusList";
import { useProposalDownload } from "./useProposalDownload";
import {
  DocumentPane,
  DownloadButton,
  EditorHeader,
  OpenError,
  PAGE_HEIGHT_CLASS,
  PANE_ROW
} from "./documentLayout";
import { ProposalTitle } from "./ProposalTitle";

interface ViewProps {
  dealId: string;
  dealName?: string;
  proposal: Proposal;
  version: ProposalHistoryVersion;
}

// One version as it went out: its file, its status, and where each recipient
// stood. Nothing on it can be changed.
const VersionView: React.FC<ViewProps> = ({
  dealId,
  dealName,
  proposal,
  version
}) => {
  const { downloading, download } = useProposalDownload();
  const shown = versionAsProposal(proposal, version);
  const fileId =
    version.status === ProposalStatus.SIGNED
      ? version.signedFileAttachmentId
      : version.sentFileAttachmentId;
  const storedFile = useStoredProposalFile(fileId);
  // Only the file that went out is shown: the composed document is the latest
  // version's, so it never stands in for an earlier one.
  const failed = !fileId || storedFile.isError;

  return (
    <>
      <EditorHeader
        dealId={dealId}
        dealName={dealName}
        title={
          <ProposalTitle
            name={proposal.name}
            editing={false}
            onRename={() => undefined}
            onDone={() => undefined}
          />
        }
        actions={
          <DownloadButton
            onClick={() =>
              void download({
                name: proposal.name,
                status: version.status,
                fileId
              })
            }
            disabled={downloading || !fileId}
          />
        }
      />
      <div className={PANE_ROW}>
        <div className="flex flex-col gap-2 w-[400px] shrink-0 overflow-y-auto">
          <RecipientStatusList
            recipients={withRecipientStates(version.recipients, shown)}
          />
        </div>
        <DocumentPane
          proposal={shown}
          url={failed ? undefined : storedFile.data?.documentUrl}
          failed={failed}
          refreshing={false}
        />
      </div>
    </>
  );
};

const VersionNotFound: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3"
      role="alert"
      data-test="proposal-version-missing"
    >
      <Text variant="bodySecondary">
        {t("contracts.proposals.editor.versionNotFound")}
      </Text>
    </div>
  );
};

// An earlier version of a proposal, opened from its row in the Proposals
// table to be read as it went out.
export const ProposalVersionPage: React.FC = () => {
  const {
    dealId = "",
    proposalId,
    versionId
  } = useParams<{ dealId: string; proposalId: string; versionId: string }>();
  const { entities: deals } = useDeals(dealId ? [dealId] : [], {
    enabled: !!dealId
  });
  const dealName = deals[0]?.name;
  const {
    data: proposals,
    isError: listError,
    refetch: refetchProposals
  } = useProposals(dealId);
  const proposal = proposals?.find((candidate) => candidate.id === proposalId);
  const {
    data: versions,
    isError: versionsError,
    refetch: refetchVersions
  } = useProposalVersions(proposal);
  // Only what the table offers.
  const version =
    versions &&
    earlierSentVersions(versions).find(
      (candidate) => candidate.id === versionId
    );
  const missing = (!!proposals && !proposal) || (!!versions && !version);

  const body = () => {
    if (proposal && version) {
      return (
        <VersionView
          dealId={dealId}
          dealName={dealName}
          proposal={proposal}
          version={version}
        />
      );
    }
    return (
      <>
        <EditorHeader dealId={dealId} dealName={dealName} />
        {listError || versionsError ? (
          <OpenError
            onRetry={() => {
              if (listError) void refetchProposals();
              if (versionsError) void refetchVersions();
            }}
          />
        ) : missing ? (
          <VersionNotFound />
        ) : (
          <div
            className="flex flex-1 items-center justify-center"
            data-test="proposal-version-loading"
          >
            <Spinner size="md" />
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={`${PAGE_HEIGHT_CLASS} w-full max-w-[1800px] mx-auto`}
      data-test="proposal-version"
    >
      {body()}
    </div>
  );
};
